/**
 * StructuredNotesEditor — Note-taking surface that looks like a textarea but
 * stores each paragraph (committed on Enter) as a discrete note object.
 *
 * Each note card supports:
 *   - Direct-quote highlighting (yellow) via regex detection of "…" pairs
 *   - Probing-question styling (blue background) for questions dropped from
 *     the questionnaire panel
 *   - Drag-grip handle for moving notes between symptom rows on the same page
 *   - A "Move" action (arrow icon) that relocates a note to a note box on a
 *     different page within the same module via the parent-supplied catalogue
 *     (moveTargets) and handler (onMoveNoteTo) — used where drag cannot reach
 *     because the destination page is not mounted
 *   - A "Tag" action (tag icon) that opens a Move-style multi-select picker of
 *     DSM / behaviour sections. Selected tags are stored on the note as
 *     `criteriaTags: string[]` so Criterion Mapping can gather notes by
 *     criterion without a second drag-to-map step
 *   - Inline editing (single-click text or pencil icon) and deletion (click X)
 *   - Empty-to-delete: clearing a note's text and pressing Enter removes it
 *   - Backspace-undo: pressing Backspace in the empty draft input pulls the
 *     last committed note back for editing
 *
 * Data model per symptom:
 *   Array<{ id: string, text: string, type: 'note' | 'question', criteriaTags?: string[] }>
 *   (IDs are opaque, collision-free strings from makeNoteId; legacy numeric
 *    IDs from older data are still honoured since IDs are only compared, never
 *    parsed.)
 *
 * Backwards-compatible: if the incoming value is a legacy HTML string (from
 * the old SmartNotesEditor), it is automatically normalised into a single-
 * element array on first render.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { PencilIcon, CheckIcon, XIcon, ArrowRightIcon, TagIcon } from '@heroicons/react/solid';
import { toast } from 'react-toastify';
import { makeNoteId } from '../../../../utils/structuredNotesMove';
import { OBS_NOTE_TAG_OPTIONS } from '../config/obsNoteTagOptions';

// ---------------------------------------------------------------------------
// Constants & utilities
// ---------------------------------------------------------------------------

/** Matches straight and curly double-quote pairs for direct-quote detection. */
const ANY_DQUOTE = '"\u201C\u201D';
const QUOTE_RE = new RegExp(
  `[${ANY_DQUOTE}]([^${ANY_DQUOTE}]+?)[${ANY_DQUOTE}]`,
  'g',
);

/** MIME types for drag-and-drop identification. */
const PROBING_Q_MIME = 'application/x-probing-question';
const OBS_NOTE_MIME = 'application/x-obs-note';
const DOMAIN_MIME = 'application/x-domain-impacted';

export { PROBING_Q_MIME, OBS_NOTE_MIME, DOMAIN_MIME };

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Strips HTML tags from a legacy SmartNotesEditor string to produce plain
 * text. Handles <br>, <div>, and <p> as newlines.
 */
function stripHtml(html) {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?(div|p)[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Normalise the incoming value into a clean notes array. Handles:
 *   - already-correct array format
 *   - legacy HTML string from SmartNotesEditor
 *   - null / undefined / empty
 */
function normaliseNotes(value) {
  if (Array.isArray(value)) {
    // Migrate legacy type:'question' notes → regular notes with a questions array
    return value.map(n => {
      if (n.type === 'question' && !n.questions) {
        return { ...n, type: 'note', questions: [n.text] };
      }
      return n;
    });
  }
  if (typeof value === 'string' && value.trim()) {
    const plain = stripHtml(value);
    if (!plain) return [];
    return [{ id: Date.now(), text: plain, type: 'note' }];
  }
  return [];
}

/** Exported for read-only history panels that seed notes from Firestore clinicianNotes. */
export function normaliseStructuredNotes(value) {
  return normaliseNotes(value);
}

/**
 * Prompt the browser to spell-check existing textarea content immediately.
 *
 * Chrome and WebKit often defer spellcheck on programmatically filled (React
 * controlled) values until the user types. Toggling spellcheck and briefly
 * selecting the full range nudges the engine to decorate misspellings as soon
 * as inline edit mode opens, without changing the note text.
 */
function refreshNativeSpellcheck(textarea) {
  if (!textarea || textarea.tagName !== 'TEXTAREA') return;

  const start = textarea.selectionStart ?? textarea.value.length;
  const end = textarea.selectionEnd ?? textarea.value.length;

  textarea.spellcheck = false;
  // Force layout so the spellcheck attribute change is applied before re-enabling.
  void textarea.offsetHeight;
  textarea.spellcheck = true;

  if (typeof textarea.forceSpellCheck === 'function') {
    try {
      textarea.forceSpellCheck();
    } catch {
      // Optional API — ignore if the browser rejects the call.
    }
  }

  if (textarea.value.length > 0) {
    textarea.setSelectionRange(0, textarea.value.length);
    textarea.setSelectionRange(start, end);
  }
}

/**
 * Returns HTML with two types of inline highlights:
 *   - Direct quotes ("…") → yellow background
 *   - Probing questions    → blue background
 *
 * Questions are matched first by exact substring, then quotes are detected
 * in the remaining segments so the two never overlap.
 *
 * `metricSafe` controls whether the highlight marks are allowed to change the
 * text's layout width. The editor renders a transparent <textarea> directly on
 * top of this highlight layer (shared CSS-grid cell), so the caret only lines
 * up when both layers produce identical glyph metrics. Bold/italic/horizontal
 * padding on a mark widens the highlighted run in this layer but NOT in the
 * plain textarea, which drags the caret left of the visible text end. When
 * `metricSafe` is true (the live editing overlays) we keep only the colour and
 * background — properties that don't affect layout — so the caret stays glued
 * to the text. Static read-only display passes false to keep the richer look
 * (italic questions, bold domains) since there is no caret to misalign there.
 */
function highlightText(text, questions = [], domains = [], metricSafe = false) {
  if (!text) return '';

  // Build a list of non-overlapping highlight ranges sorted by position.
  // Each range: { start, end, type: 'question' | 'quote' | 'domain' }
  const ranges = [];

  // 1a) Find domain-impacted substrings (brand-primary highlight)
  (domains || []).forEach(d => {
    const idx = text.indexOf(d);
    if (idx !== -1) {
      ranges.push({ start: idx, end: idx + d.length, type: 'domain' });
    }
  });

  // 1b) Find question substrings
  (questions || []).forEach(q => {
    const idx = text.indexOf(q);
    if (idx !== -1) {
      const overlaps = ranges.some(r => idx < r.end && (idx + q.length) > r.start);
      if (!overlaps) {
        ranges.push({ start: idx, end: idx + q.length, type: 'question' });
      }
    }
  });

  // 2) Find quote substrings in regions not already claimed
  QUOTE_RE.lastIndex = 0;
  let match;
  while ((match = QUOTE_RE.exec(text)) !== null) {
    const s = match.index;
    const e = s + match[0].length;
    const overlaps = ranges.some(r => s < r.end && e > r.start);
    if (!overlaps) {
      ranges.push({ start: s, end: e, type: 'quote' });
    }
  }

  ranges.sort((a, b) => a.start - b.start);

  // 3) Build highlighted HTML
  if (ranges.length === 0) return escapeHtml(text);

  let html = '';
  let cursor = 0;
  ranges.forEach(r => {
    if (r.start > cursor) html += escapeHtml(text.slice(cursor, r.start));
    const seg = escapeHtml(text.slice(r.start, r.end));
    if (r.type === 'domain') {
      // Bold + horizontal padding are dropped in metric-safe mode so the run
      // keeps the same width as the plain textarea behind the caret.
      const domainStyle = metricSafe
        ? 'background:rgba(100,46,108,0.15);color:#642e6c;border-radius:3px;padding:0;box-decoration-break:clone'
        : 'background:rgba(100,46,108,0.15);color:#642e6c;border-radius:3px;font-weight:600;padding:0 2px;box-decoration-break:clone';
      html += `<mark style="${domainStyle}">${seg}</mark>`;
    } else if (r.type === 'question') {
      // Italic changes glyph advance widths, so it is dropped in metric-safe mode.
      const questionStyle = metricSafe
        ? 'background:rgba(191,219,254,0.6);color:#1d4ed8;border-radius:3px;padding:0;box-decoration-break:clone'
        : 'background:rgba(191,219,254,0.6);color:#1d4ed8;border-radius:3px;font-style:italic;padding:0;box-decoration-break:clone';
      html += `<mark style="${questionStyle}">${seg}</mark>`;
    } else {
      html += `<mark style="background:rgba(251,191,36,0.25);color:#78350f;border-radius:2px;padding:0;box-decoration-break:clone">${seg}</mark>`;
    }
    cursor = r.end;
  });
  if (cursor < text.length) html += escapeHtml(text.slice(cursor));

  return html;
}

// ---------------------------------------------------------------------------
// NoteCard — a single note with grip handle, inline highlighting, edit/delete
// ---------------------------------------------------------------------------

function NoteCard({
  note,
  domainId,
  onUpdate,
  onDelete,
  onUpdateTags = null,
  readOnly = false,
  // When true, the grip stays draggable even though the card cannot be edited.
  // Used by Observation Criterion Mapping: the master-notes inbox is locked for
  // typing/deleting, but clinicians still need to drag those cards into DSM rows.
  allowDragWhileReadOnly = false,
  /**
   * Keep the Tag picker available while `readOnly` is true. Used by Criterion
   * Mapping's tagged-note panels so clinicians can re-tag without leaving the
   * phase (edit / delete / Move stay locked).
   */
  allowTagWhileReadOnly = false,
  isMapped = false,
  /**
   * When true, the card plays a short collapse/fade exit (Criterion Mapping
   * untag of the current page). Parent owns removing the note from data after
   * the animation window.
   */
  isExiting = false,
  moveTargets = null,
  currentMoveId = null,
  onMoveNoteTo = null,
  // When true, show the Tag picker so clinicians can attach DSM / behaviour
  // section labels to this note without leaving Task Administration / ADOS.
  enableCriteriaTagging = false,
  tagOptions = OBS_NOTE_TAG_OPTIONS,
  /**
   * Optional tag id that must not be removed without parent confirmation
   * (e.g. the Criterion Mapping page the clinician is currently on). When a
   * toggle would drop this id, we call `onRequestRemoveProtectedTag` instead
   * of applying the change immediately.
   */
  protectedTagId = null,
  onRequestRemoveProtectedTag = null,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.text);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const inputRef = useRef(null);
  const confirmTimer = useRef(null);
  const cursorPosRef = useRef(null);
  const cardRef = useRef(null);

  // ---- Move-to-another-box popover ---------------------------------------
  // A note can be relocated to any other note box in the same module (e.g. a
  // different History section, SSI criterion, or Observation task). Because the
  // destination box lives on a different page that is not mounted, this cannot
  // be a drag — it is an explicit picker. The parent supplies the catalogue of
  // destinations (`moveTargets`) and the move handler (`onMoveNoteTo`).
  const [movePickerOpen, setMovePickerOpen] = useState(false);
  const [moveFilter, setMoveFilter] = useState('');
  const movePickerRef = useRef(null);
  const moveBtnRef = useRef(null);
  const moveFilterRef = useRef(null);

  // ---- Tag-to-criterion popover ------------------------------------------
  // Same visual pattern as Move (filterable dropdown), but multi-select: each
  // click toggles a DSM / behaviour section on the note's criteriaTags array.
  // The picker stays open so clinicians can tag one note to several sections
  // in one pass (e.g. a note that speaks to both A1 and A2).
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [tagFilter, setTagFilter] = useState('');
  const tagPickerRef = useRef(null);
  const tagBtnRef = useRef(null);
  const tagFilterRef = useRef(null);

  // Destinations excluding this very box (you cannot move a note onto itself).
  const availableMoveTargets = useMemo(() => {
    if (!Array.isArray(moveTargets)) return [];
    return moveTargets.filter((t) => t && t.id !== currentMoveId);
  }, [moveTargets, currentMoveId]);

  const canMove = !readOnly && typeof onMoveNoteTo === 'function' && availableMoveTargets.length > 0;
  const canTag = enableCriteriaTagging
    && typeof onUpdateTags === 'function'
    && Array.isArray(tagOptions)
    && tagOptions.length > 0
    && (!readOnly || allowTagWhileReadOnly);
  // Show the action strip whenever edit/move/delete OR tag-while-readOnly is live.
  const showActions = (!readOnly || canTag) && !isExiting;

  const selectedTags = Array.isArray(note.criteriaTags) ? note.criteriaTags : [];

  /**
   * Drag is off while editing (the grip would fight the textarea). It is also
   * off in ordinary read-only mode — every drop target on a locked page already
   * rejects the drop, so a grab cursor would only promise an interaction that
   * cannot complete (e.g. a task marked "Not administered").
   *
   * Exception: `allowDragWhileReadOnly` keeps the grip live when the box is
   * locked for editing but is still a valid *source* for drops elsewhere —
   * Criterion Mapping's Observation Notes inbox is the main case.
   */
  const canDrag = !editing && (!readOnly || allowDragWhileReadOnly);

  // Apply the free-text filter, then group destinations under their page label
  // so the list reads as "Page > box" — matching how clinicians think about it.
  const groupedMoveTargets = useMemo(() => {
    const needle = moveFilter.trim().toLowerCase();
    const matches = needle
      ? availableMoveTargets.filter((t) =>
          `${t.group || ''} ${t.label || ''}`.toLowerCase().includes(needle))
      : availableMoveTargets;

    const groups = [];
    const indexByGroup = new Map();
    for (const t of matches) {
      const groupLabel = t.group || '';
      if (!indexByGroup.has(groupLabel)) {
        indexByGroup.set(groupLabel, groups.length);
        groups.push({ group: groupLabel, options: [] });
      }
      groups[indexByGroup.get(groupLabel)].options.push(t);
    }
    return groups;
  }, [availableMoveTargets, moveFilter]);

  // Filtered tag options for the Tag picker (flat list — no groups needed).
  const filteredTagOptions = useMemo(() => {
    const needle = tagFilter.trim().toLowerCase();
    if (!needle) return tagOptions || [];
    return (tagOptions || []).filter((t) =>
      `${t.id || ''} ${t.label || ''}`.toLowerCase().includes(needle));
  }, [tagOptions, tagFilter]);

  // Close the popover on any outside click (mousedown so it beats other handlers).
  useEffect(() => {
    if (!movePickerOpen) return undefined;
    const handlePointerDown = (e) => {
      if (movePickerRef.current?.contains(e.target)) return;
      if (moveBtnRef.current?.contains(e.target)) return;
      setMovePickerOpen(false);
      setMoveFilter('');
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [movePickerOpen]);

  useEffect(() => {
    if (!tagPickerOpen) return undefined;
    const handlePointerDown = (e) => {
      if (tagPickerRef.current?.contains(e.target)) return;
      if (tagBtnRef.current?.contains(e.target)) return;
      setTagPickerOpen(false);
      setTagFilter('');
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [tagPickerOpen]);

  // Focus the filter input when the popover opens for quick keyboard use.
  useEffect(() => {
    if (movePickerOpen) {
      requestAnimationFrame(() => moveFilterRef.current?.focus());
    }
  }, [movePickerOpen]);

  useEffect(() => {
    if (tagPickerOpen) {
      requestAnimationFrame(() => tagFilterRef.current?.focus());
    }
  }, [tagPickerOpen]);

  /** Toggle a single tag id on/off for this note (multi-select). */
  const handleTagToggle = (tagId) => {
    const current = Array.isArray(note.criteriaTags) ? note.criteriaTags : [];
    const next = current.includes(tagId)
      ? current.filter((id) => id !== tagId)
      : [...current, tagId];

    // Untagging the "protected" criterion (the page the clinician is on in
    // Criterion Mapping) removes the note from that page — ask the parent to
    // confirm before we apply, so they can cancel without a flash of removal.
    const removesProtected = protectedTagId
      && current.includes(protectedTagId)
      && !next.includes(protectedTagId);
    if (removesProtected && typeof onRequestRemoveProtectedTag === 'function') {
      setTagPickerOpen(false);
      setTagFilter('');
      onRequestRemoveProtectedTag(note.id, next, protectedTagId);
      return;
    }

    onUpdateTags(note.id, next);
  };

  const handleMoveSelect = (target) => {
    setMovePickerOpen(false);
    setMoveFilter('');

    // If the note is mid-edit, fold the draft into the move so the relocated
    // note carries the clinician's latest wording (same intent as Save first).
    let notePatch = null;
    if (editing) {
      const trimmed = draft.trim();
      if (!trimmed) {
        onDelete(note.id);
        return;
      }
      if (trimmed !== note.text) {
        notePatch = { text: trimmed };
      }
      setEditing(false);
    }

    onMoveNoteTo(currentMoveId, note.id, target.id, undefined, notePatch);
    // The destination is off-screen, so confirm the move explicitly.
    const destination = target.group ? `${target.group} \u2022 ${target.label}` : target.label;
    toast.success(`Note moved to ${destination}`);
  };

  useEffect(() => {
    if (!editing || !inputRef.current) return;
    const el = inputRef.current;
    requestAnimationFrame(() => {
      el.focus();
      if (cursorPosRef.current !== null) {
        const pos = Math.min(cursorPosRef.current, el.value.length);
        el.setSelectionRange(pos, pos);
        cursorPosRef.current = null;
      } else {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
      refreshNativeSpellcheck(el);
    });
  }, [editing]);

  // Clear the auto-dismiss timer on unmount
  useEffect(() => () => { if (confirmTimer.current) clearTimeout(confirmTimer.current); }, []);

  const commitEdit = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      // An empty note is semantically void — delete it rather than
      // silently restoring the old text. Return early because onDelete
      // triggers a parent re-render that unmounts this NoteCard.
      onDelete(note.id);
      return;
    }
    if (trimmed !== note.text) {
      onUpdate(note.id, trimmed);
    }
    setDraft(note.text);
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitEdit(); return; }
    if (e.key === 'Enter' && e.shiftKey) {
      // Intercept Shift+Enter so the newline is inserted via React state
      // rather than natively by the browser. This keeps the textarea and
      // highlight layer perfectly in sync — no one-frame cursor desync.
      e.preventDefault();
      const el = e.target;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = draft.slice(0, start) + '\n' + draft.slice(end);
      setDraft(next);
      requestAnimationFrame(() => el.setSelectionRange(start + 1, start + 1));
      return;
    }
    if (e.key === 'Escape') { setDraft(note.text); setEditing(false); }
  };

  const handleDragStart = (e) => {
    e.dataTransfer.setData(
      OBS_NOTE_MIME,
      JSON.stringify({ sourceDomainId: domainId, noteId: note.id }),
    );
    e.dataTransfer.effectAllowed = 'move';
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      e.dataTransfer.setDragImage(cardRef.current, e.clientX - rect.left, e.clientY - rect.top);
    }
  };

  /**
   * Single-click enters edit mode with the cursor placed at the exact
   * character the user clicked on. Uses the browser's caret-from-point
   * API to resolve the click coordinates to a text node + local offset,
   * then walks the span's DOM to compute the total plain-text offset
   * (since highlights split the text across multiple child nodes).
   *
   * Guards against click-drag text selection: if the user dragged to
   * select text (e.g. for copy), the selection is non-collapsed and we
   * skip entering edit mode so the selection is preserved.
   */
  const handleTextClick = (e) => {
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed) return;

    const span = e.currentTarget;
    let totalOffset = note.text.length; // fallback: end of text

    let targetNode = null;
    let localOffset = 0;

    if (document.caretPositionFromPoint) {
      const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
      if (pos) { targetNode = pos.offsetNode; localOffset = pos.offset; }
    } else if (document.caretRangeFromPoint) {
      const range = document.caretRangeFromPoint(e.clientX, e.clientY);
      if (range) { targetNode = range.startContainer; localOffset = range.startOffset; }
    }

    if (targetNode) {
      let charsSoFar = 0;
      const walker = document.createTreeWalker(span, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        if (node === targetNode) {
          totalOffset = charsSoFar + localOffset;
          break;
        }
        charsSoFar += node.textContent.length;
      }
    }

    cursorPosRef.current = totalOffset;
    setDraft(note.text);
    setEditing(true);
  };

  /** First press shows "Delete?", second press confirms. Auto-dismisses after 3s. */
  const handleDeleteClick = () => {
    if (confirmingDelete) {
      if (confirmTimer.current) clearTimeout(confirmTimer.current);
      setConfirmingDelete(false);
      onDelete(note.id);
    } else {
      setConfirmingDelete(true);
      confirmTimer.current = setTimeout(() => setConfirmingDelete(false), 3000);
    }
  };

  // Tag / Move popovers are position:absolute inside this card. Later sibling
  // cards paint on top in DOM order unless we raise this card's stacking
  // context while a picker is open — otherwise note text "ghosts" through the
  // menu (same bug on both dropdowns).
  const pickerOpen = tagPickerOpen || movePickerOpen;

  return (
    <div
      ref={cardRef}
      data-note-card
      className={`group flex items-start gap-1 px-2 py-px rounded-lg text-left min-w-0
        transition-[opacity,transform,max-height,margin,padding] duration-300 ease-out
        ${isExiting
          ? 'opacity-0 -translate-y-1 max-h-0 py-0 my-0 overflow-hidden pointer-events-none'
          : 'opacity-100 translate-y-0 max-h-[40rem]'}
        ${pickerOpen ? 'relative z-20' : 'relative'}
        ${isMapped && !isExiting ? 'bg-emerald-50/70 hover:bg-emerald-50' : ''}
        ${!isMapped && !isExiting ? 'hover:bg-gray-50' : ''}`}
    >
      {/* Drag grip / bullet / mapped checkmark.
           When mapped: shows a checkmark by default, crossfades to drag grip on hover
           (note can still be dragged to additional criteria).
           When not mapped: shows a bullet by default, crossfades to drag grip on hover. */}
      <span
        draggable={canDrag}
        onDragStart={canDrag ? handleDragStart : undefined}
        className={`flex-shrink-0 relative w-3.5 h-3.5 select-none mt-1.5
          ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''}`}
        aria-hidden="true"
      >
        {isMapped ? (
          /* Checkmark — fades out on hover to reveal drag grip */
          <svg
            className="absolute inset-0 w-3.5 h-3.5 text-emerald-500 transition-opacity duration-200
              opacity-100 group-hover:opacity-0"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          /* Bullet — fades out on hover */
          <svg
            className="absolute inset-0 w-3.5 h-3.5 text-gray-300 transition-opacity duration-200
              opacity-100 group-hover:opacity-0"
            viewBox="0 0 16 16" fill="currentColor"
          >
            <circle cx="8" cy="8" r="3" />
          </svg>
        )}
        {/* Six-dot grip — fades in on hover for all states */}
        <svg
          className="absolute inset-0 w-3.5 h-3.5 text-gray-400 transition-opacity duration-200
            opacity-0 group-hover:opacity-100"
          viewBox="0 0 16 16" fill="currentColor"
        >
          <circle cx="5" cy="3" r="1.5" />
          <circle cx="11" cy="3" r="1.5" />
          <circle cx="5" cy="8" r="1.5" />
          <circle cx="11" cy="8" r="1.5" />
          <circle cx="5" cy="13" r="1.5" />
          <circle cx="11" cy="13" r="1.5" />
        </svg>
      </span>

      {/* Note text + optional tag chips. Editing uses the layered highlight
           editor; chips sit under the text so tagging never fights the caret. */}
      <div className="flex-1 min-w-0">
        {editing && !readOnly ? (
          <div
            className="rounded border border-primary/30 ring-1 ring-primary/40 bg-white"
            style={{ display: 'grid' }}
          >
            {/* Highlight layer — sits behind the transparent textarea.
                 Trailing newlines are collapsed by browsers even with pre-wrap,
                 so we append a non-breaking space to force the last line open. */}
            <div
              aria-hidden="true"
              className="text-sm leading-5 px-1.5 py-px whitespace-pre-wrap break-words pointer-events-none text-gray-800"
              style={{ gridArea: '1/1' }}
              dangerouslySetInnerHTML={{
                __html: (highlightText(draft, note.questions, note.domains, true) || '&nbsp;') + (draft.endsWith('\n') ? '\n\u00a0' : ''),
              }}
            />
            {/* Transparent textarea on top — Shift+Enter inserts a newline, plain Enter commits.
                 spellCheck stays on; CSS below keeps browser spelling/grammar wavy lines visible
                 even though the typed glyphs are transparent (highlight layer shows the text). */}
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={handleKeyDown}
              spellCheck
              className="structured-notes-spellcheck-input text-sm leading-5 bg-transparent px-1.5 py-px focus:outline-none resize-none overflow-hidden"
              style={{ gridArea: '1/1', caretColor: '#374151' }}
            />
          </div>
        ) : (
          <span
            onClick={readOnly ? undefined : handleTextClick}
            className={`block text-sm text-left leading-5 py-px whitespace-pre-wrap ${isMapped ? 'text-gray-500' : 'text-gray-700'} ${readOnly ? 'cursor-default' : 'cursor-text'}`}
            style={{ overflowWrap: 'anywhere' }}
            dangerouslySetInnerHTML={{
              __html: highlightText(note.text, note.questions, note.domains),
            }}
          />
        )}

        {/* Compact chips so clinicians can see which criteria this note feeds
            without reopening the Tag picker. Shown in read-only too (Criterion
            Mapping panels) so mapped-from-tag notes stay self-explanatory. */}
        {selectedTags.length > 0 && (
          // Compact, low-contrast chips so tags read as metadata rather than
          // competing with the note text for attention.
          <div className="flex flex-wrap gap-x-1 gap-y-0.5 mt-px leading-none" data-note-tags>
            {selectedTags.map((tagId) => {
              const opt = (tagOptions || []).find((t) => t.id === tagId);
              const chipLabel = opt?.label || tagId;
              // A catalogue can name the chip itself (CYP observation uses
              // the symptom-list prefix, "A 1" / "H/I 3", while the stored
              // id stays the DSM code). Otherwise prefer the short code
              // (A1, B2, SR…) so a note tagged to several sections stays
              // compact.
              const shortLabel = opt?.chipLabel
                || (/^[AB]\d$/.test(tagId)
                  ? tagId
                  : (tagId === 'self_reported' ? 'SR' : tagId === 'other' ? 'OB' : chipLabel));
              return (
                <span
                  key={tagId}
                  title={chipLabel}
                  className="inline-flex items-center px-1 rounded-sm text-[9px] font-semibold
                    leading-none h-3.5 bg-primary/[0.06] text-primary/70"
                >
                  {shortLabel}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Action buttons — full CRUD when editable; Tag-only when
           allowTagWhileReadOnly (Criterion Mapping re-tag panels). */}
      {showActions && (
        <div className={`flex items-center gap-0.5 flex-shrink-0 mt-0.5
          transition-opacity duration-200 ease-in-out
          ${editing || pickerOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
        >
          {!readOnly && (editing ? (
            <button
              onClick={commitEdit}
              className="p-0.5 rounded text-green-600 hover:bg-green-100 transition-colors duration-150"
              aria-label="Save edit"
            >
              <CheckIcon className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              onClick={() => { cursorPosRef.current = null; setEditing(true); }}
              className="p-0.5 rounded text-gray-400 hover:bg-gray-200 hover:text-gray-600 transition-colors duration-150"
              aria-label="Edit note"
              title="Edit"
            >
              <PencilIcon className="w-3 h-3" />
            </button>
          ))}

          {/* Tag — multi-select DSM / behaviour sections. Same dropdown chrome
               as Move so the action feels familiar; stays open across toggles. */}
          {canTag && (
            <div className="relative">
              <button
                ref={tagBtnRef}
                onClick={() => {
                  setTagPickerOpen((open) => !open);
                  setTagFilter('');
                  // Only one popover at a time — closing Move avoids two menus
                  // fighting for the same corner of the card.
                  setMovePickerOpen(false);
                }}
                className={`p-0.5 rounded transition-colors duration-150
                  ${tagPickerOpen || selectedTags.length > 0
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-400 hover:bg-gray-200 hover:text-gray-600'}`}
                aria-label="Tag note with criteria"
                aria-haspopup="menu"
                aria-expanded={tagPickerOpen}
                title="Tag"
              >
                <TagIcon className="w-3 h-3" />
              </button>

              {tagPickerOpen && (
                <div
                  ref={tagPickerRef}
                  role="menu"
                  className="absolute right-0 top-full mt-1 z-30 w-72 max-h-72 overflow-hidden
                    flex flex-col rounded-lg border border-gray-200 bg-white shadow-xl text-left
                    isolate"
                >
                  <div className="px-2 py-1.5 border-b border-gray-100">
                    <p className="text-[11px] font-semibold text-gray-500 px-1 pb-1">Tag note with</p>
                    <input
                      ref={tagFilterRef}
                      type="text"
                      value={tagFilter}
                      onChange={(e) => setTagFilter(e.target.value)}
                      placeholder="Search criteria..."
                      className="w-full px-2 py-1 text-xs border border-gray-200 rounded
                        focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40"
                    />
                  </div>

                  <div className="flex-1 overflow-y-auto py-1">
                    {filteredTagOptions.length === 0 ? (
                      <p className="px-3 py-2 text-xs text-gray-400 italic">No matching criteria</p>
                    ) : (
                      filteredTagOptions.map((opt) => {
                        const isSelected = selectedTags.includes(opt.id);
                        return (
                          <button
                            key={opt.id}
                            role="menuitemcheckbox"
                            aria-checked={isSelected}
                            onClick={() => handleTagToggle(opt.id)}
                            className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2
                              transition-colors duration-100
                              ${isSelected
                                ? 'bg-primary/5 text-primary font-medium'
                                : 'text-gray-700 hover:bg-primary/5 hover:text-primary'}`}
                          >
                            <span
                              className={`flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center
                                ${isSelected
                                  ? 'bg-primary border-primary text-white'
                                  : 'border-gray-300 bg-white'}`}
                              aria-hidden="true"
                            >
                              {isSelected && <CheckIcon className="w-2.5 h-2.5" />}
                            </span>
                            <span className="min-w-0">{opt.label}</span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Move — opens a picker of note boxes on other pages within this
               module. Stays available while editing (like Delete); any draft
               text is applied to the moved note automatically. */}
          {canMove && (
            <div className="relative">
              <button
                ref={moveBtnRef}
                onClick={() => {
                  setMovePickerOpen(true);
                  setMoveFilter('');
                  setTagPickerOpen(false);
                }}
                className={`p-0.5 rounded transition-colors duration-150
                  ${movePickerOpen
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-400 hover:bg-gray-200 hover:text-gray-600'}`}
                aria-label="Move note to another section"
                aria-haspopup="menu"
                aria-expanded={movePickerOpen}
                title="Move"
              >
                <ArrowRightIcon className="w-3 h-3" />
              </button>

              {movePickerOpen && (
                <div
                  ref={movePickerRef}
                  role="menu"
                  className="absolute right-0 top-full mt-1 z-30 w-64 max-h-72 overflow-hidden
                    flex flex-col rounded-lg border border-gray-200 bg-white shadow-xl text-left
                    isolate"
                >
                  <div className="px-2 py-1.5 border-b border-gray-100">
                    <p className="text-[11px] font-semibold text-gray-500 px-1 pb-1">Move note to</p>
                    <input
                      ref={moveFilterRef}
                      type="text"
                      value={moveFilter}
                      onChange={(e) => setMoveFilter(e.target.value)}
                      placeholder="Search sections..."
                      className="w-full px-2 py-1 text-xs border border-gray-200 rounded
                        focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40"
                    />
                  </div>

                  <div className="flex-1 overflow-y-auto py-1">
                    {groupedMoveTargets.length === 0 ? (
                      <p className="px-3 py-2 text-xs text-gray-400 italic">No matching sections</p>
                    ) : (
                      groupedMoveTargets.map((grp) => (
                        <div key={grp.group || '_'} className="mb-0.5">
                          {grp.group && (
                            <p className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                              {grp.group}
                            </p>
                          )}
                          {grp.options.map((target) => (
                            <button
                              key={target.id}
                              role="menuitem"
                              onClick={() => handleMoveSelect(target)}
                              className="w-full text-left px-3 py-1.5 text-xs text-gray-700
                                hover:bg-primary/5 hover:text-primary transition-colors duration-100"
                            >
                              {target.label}
                            </button>
                          ))}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Delete — morphs from X icon to "Delete?" label */}
          {!readOnly && (
            <button
              onClick={handleDeleteClick}
              onBlur={() => { setConfirmingDelete(false); if (confirmTimer.current) clearTimeout(confirmTimer.current); }}
              onMouseLeave={() => { if (confirmingDelete) { setConfirmingDelete(false); if (confirmTimer.current) clearTimeout(confirmTimer.current); } }}
              className={`
                rounded inline-flex items-center justify-center
                transition-all duration-200 ease-in-out
                ${confirmingDelete
                  ? 'px-1.5 py-0.5 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'
                  : 'p-0.5 bg-transparent text-gray-400 hover:bg-red-100 hover:text-red-500'}
              `}
              aria-label={confirmingDelete ? 'Confirm delete' : 'Delete note'}
            >
              {confirmingDelete
                ? <span className="text-[10px] font-semibold whitespace-nowrap leading-3">Delete?</span>
                : <XIcon className="w-3 h-3" />
              }
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function StructuredNotesEditor({
  notes: rawNotes,
  onChange,
  placeholder = 'Type a note and press Enter…',
  readOnlyPlaceholder = 'Notes can only be added by the assigned clinician',
  minHeight = '7.5rem',
  domainId,
  onMoveNoteIn,
  readOnly = false,
  /**
   * Keep note grips draggable while `readOnly` is true. Does not re-enable
   * typing, delete, or the Move picker — only the HTML5 drag source. See
   * NoteCard for the clinical rationale (Criterion Mapping inbox).
   */
  allowDragWhileReadOnly = false,
  /**
   * Keep Tag available while `readOnly` — Criterion Mapping re-tag panels.
   * Does not re-enable typing, delete, or Move.
   */
  allowTagWhileReadOnly = false,
  mappedNoteIds = null,
  /**
   * Note ids currently playing the exit animation (e.g. just untagged from
   * the Criterion Mapping page). Cards stay mounted until the parent clears
   * them after the transition.
   */
  exitingNoteIds = null,
  onProbingQuestionDropped,
  moveTargets = null,
  currentMoveId = null,
  onMoveNoteTo = null,
  /**
   * When true, each note card shows a Tag action that multi-selects from
   * `tagOptions` (defaults to the OBS DSM / behaviour catalogue). Tags are
   * persisted on the note as `criteriaTags`.
   */
  enableCriteriaTagging = false,
  tagOptions = OBS_NOTE_TAG_OPTIONS,
  /**
   * Optional tags stamped onto every newly committed note (e.g. ADOS items
   * auto-tag to their mapped DSM criterion). Clinicians can still amend via
   * the Tag picker. Existing notes are never rewritten by this prop.
   */
  defaultCriteriaTags = null,
  /**
   * When provided, tag updates go through this callback instead of rewriting
   * the local notes array via `onChange`. Needed when the editor is showing a
   * filtered view of notes that actually live in Task Admin / ADOS storage.
   * Signature: (noteId, criteriaTags) => void
   */
  onUpdateNoteTags = null,
  /**
   * Tag id that requires confirmation before removal (Criterion Mapping page
   * id). Paired with `onRequestRemoveProtectedTag`.
   */
  protectedTagId = null,
  onRequestRemoveProtectedTag = null,
}) {
  const notes = normaliseNotes(rawNotes);

  const [draft, setDraft] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const draftRef = useRef(null);
  const containerRef = useRef(null);
  const notesListRef = useRef(null);

  /**
   * While a note card is being dragged over this editor, tracks the index
   * (0 … notes.length) where the insertion indicator should appear.
   * null when no note drag is active over this editor.
   */
  const [dropInsertIndex, setDropInsertIndex] = useState(null);

  /**
   * Tracks probing-question substrings that have been dropped into
   * the current draft. Cleared when the note is committed.
   */
  const [draftQuestions, setDraftQuestions] = useState([]);

  /**
   * Tracks domain-impacted substrings dropped into the current draft.
   * These are highlighted in the brand-primary colour to visually
   * distinguish them from probing questions (blue) and quotes (yellow).
   */
  const [draftDomains, setDraftDomains] = useState([]);

  /**
   * Tags carried on a note that was pulled back into the draft via
   * Backspace-undo. Without this, re-committing the draft would drop the
   * clinician's tags (or overwrite them with defaultCriteriaTags).
   */
  const [draftCriteriaTags, setDraftCriteriaTags] = useState(undefined);

  /**
   * Live-highlighted HTML for the draft input. Shows domain highlights
   * (primary), probing-question highlights (blue), and quote highlights (yellow).
   */
  const draftHighlightedHtml = useMemo(() => {
    if (!draft) return '';
    // metricSafe=true: this overlay sits under the live caret, so the marks
    // must not change text width or the caret drifts left of the visible text.
    return highlightText(draft, draftQuestions, draftDomains, true);
  }, [draft, draftQuestions, draftDomains]);

  /**
   * Emit the normalised array on first render if the incoming value was
   * a legacy string — this "upgrades" the storage format in-place.
   */
  const hasNormalised = useRef(false);
  useEffect(() => {
    if (hasNormalised.current) return;
    if (rawNotes && typeof rawNotes === 'string') {
      hasNormalised.current = true;
      const migrated = normaliseNotes(rawNotes);
      if (migrated.length > 0) onChange(migrated);
    }
  }, [rawNotes, onChange]);

  // ---- CRUD helpers -------------------------------------------------------

  const addNote = useCallback(() => {
    const text = draft.trim();
    if (!text) return;
    // Prefer tags restored from Backspace-undo; otherwise stamp the caller's
    // defaults (ADOS auto-tag). An empty defaultCriteriaTags array is treated
    // as "no auto-tag" — same as null — so callers can pass [] safely.
    const restoredTags = Array.isArray(draftCriteriaTags) ? draftCriteriaTags : undefined;
    const autoTags = !restoredTags
      && Array.isArray(defaultCriteriaTags)
      && defaultCriteriaTags.length > 0
      ? [...defaultCriteriaTags]
      : undefined;
    const criteriaTags = restoredTags ?? autoTags;
    const newNote = {
      id: makeNoteId(), text, type: 'note',
      questions: draftQuestions.length > 0 ? [...draftQuestions] : undefined,
      domains: draftDomains.length > 0 ? [...draftDomains] : undefined,
      ...(criteriaTags ? { criteriaTags } : {}),
    };
    onChange([...notes, newNote]);
    setDraft('');
    setDraftQuestions([]);
    setDraftDomains([]);
    setDraftCriteriaTags(undefined);
  }, [draft, draftQuestions, draftDomains, draftCriteriaTags, notes, onChange, defaultCriteriaTags]);

  const updateNote = useCallback((noteId, newText) => {
    onChange(notes.map(n => n.id === noteId ? { ...n, text: newText } : n));
  }, [notes, onChange]);

  const updateNoteTags = useCallback((noteId, criteriaTags) => {
    // Prefer the external handler when the editor is a filtered view of notes
    // owned elsewhere (Criterion Mapping → Task Admin / ADOS arrays).
    if (typeof onUpdateNoteTags === 'function') {
      onUpdateNoteTags(noteId, criteriaTags);
      return;
    }
    if (typeof onChange !== 'function') return;
    onChange(notes.map((n) => (
      n.id === noteId ? { ...n, criteriaTags } : n
    )));
  }, [notes, onChange, onUpdateNoteTags]);

  const deleteNote = useCallback((noteId) => {
    onChange(notes.filter(n => n.id !== noteId));
  }, [notes, onChange]);

  // ---- Click-anywhere-to-focus --------------------------------------------

  /**
   * Focuses the draft input when the user clicks on empty space in the
   * container (padding, gaps between notes, the area below the last note).
   * Clicks on NoteCards, buttons, and textareas are excluded so they can
   * handle their own interactions (e.g. single-click-to-edit on a note).
   */
  const handleContainerClick = useCallback((e) => {
    if (readOnly) return;
    if (e.target.closest('[data-note-card]') || e.target.closest('button') || e.target.closest('textarea')) return;
    draftRef.current?.focus();
  }, [readOnly]);

  // ---- Drag-and-drop (incoming) -------------------------------------------

  /**
   * Computes the insertion index (0 … notes.length) closest to the mouse's
   * Y position within the notes list. Checks the vertical midpoint of each
   * note card: if the cursor is above the midpoint, the indicator goes before
   * that card; otherwise after it. This gives the most intuitive feel.
   */
  const calcInsertIndex = useCallback((clientY) => {
    const list = notesListRef.current;
    if (!list) return notes.length;
    const children = Array.from(list.children).filter(
      el => !el.dataset.insertIndicator,
    );
    for (let i = 0; i < children.length; i++) {
      const rect = children[i].getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) return i;
    }
    return children.length;
  }, [notes.length]);

  const handleDragOver = (e) => {
    if (readOnly) return;
    const hasNote = e.dataTransfer.types.includes(OBS_NOTE_MIME);
    const hasQuestion = e.dataTransfer.types.includes(PROBING_Q_MIME);
    const hasDomain = e.dataTransfer.types.includes(DOMAIN_MIME);
    if (hasNote || hasQuestion || hasDomain) {
      e.preventDefault();
      e.dataTransfer.dropEffect = hasNote ? 'move' : 'copy';
      setIsDragOver(true);
      if (hasNote) {
        setDropInsertIndex(calcInsertIndex(e.clientY));
      }
    }
  };

  const handleDragLeave = (e) => {
    if (containerRef.current && !containerRef.current.contains(e.relatedTarget)) {
      setIsDragOver(false);
      setDropInsertIndex(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (readOnly) return;
    const insertAt = dropInsertIndex;
    setIsDragOver(false);
    setDropInsertIndex(null);

    // Handle domain-impacted drop — insert the domain label into the
    // draft so the clinician can continue typing context after it.
    const domainText = e.dataTransfer.getData(DOMAIN_MIME);
    if (domainText) {
      setDraft(prev => {
        const prefix = prev.length > 0 && !prev.endsWith(' ') ? ' ' : '';
        return prev + prefix + domainText;
      });
      setDraftDomains(prev => [...prev, domainText.trim()]);
      setTimeout(() => { if (draftRef.current) draftRef.current.focus(); }, 0);
      return;
    }

    // Handle probing question drop — insert into the draft input so
    // the clinician can continue typing after the question text, and
    // notify the parent so it can auto-mark the question as "asked".
    const questionText = e.dataTransfer.getData(PROBING_Q_MIME);
    if (questionText) {
      setDraft(prev => {
        const prefix = prev.length > 0 && !prev.endsWith(' ') ? ' ' : '';
        return prev + prefix + questionText + ' ';
      });
      setDraftQuestions(prev => [...prev, questionText]);
      if (onProbingQuestionDropped) onProbingQuestionDropped(questionText);
      setTimeout(() => { if (draftRef.current) draftRef.current.focus(); }, 0);
      return;
    }

    // Handle note card drop — reorder within same box or move from another
    const noteData = e.dataTransfer.getData(OBS_NOTE_MIME);
    if (noteData) {
      try {
        const { sourceDomainId, noteId } = JSON.parse(noteData);

        if (sourceDomainId === domainId) {
          // Same-box reorder: remove the note from its current position
          // and splice it in at the drop target index.
          const noteObj = notes.find(n => n.id === noteId);
          if (!noteObj) return;
          const without = notes.filter(n => n.id !== noteId);
          const oldIdx = notes.indexOf(noteObj);
          // Adjust target index when moving a note downward (since removing
          // the original shifts indices above the drop point).
          const targetIdx = insertAt != null
            ? (oldIdx < insertAt ? insertAt - 1 : insertAt)
            : without.length;
          const reordered = [...without];
          reordered.splice(targetIdx, 0, noteObj);
          onChange(reordered);
        } else {
          // Cross-box move: delegate to the parent's move handler, then
          // reposition within this box at the indicated index.
          if (onMoveNoteIn) {
            onMoveNoteIn(sourceDomainId, noteId, domainId, insertAt);
          }
        }
      } catch { /* malformed drag data */ }
    }
  };

  // ---- Render -------------------------------------------------------------

  return (
    <div
      ref={containerRef}
      onClick={handleContainerClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`
        rounded-lg border text-left transition-all duration-150
        ${readOnly ? 'bg-gray-100 border-gray-200' : ''}
        ${isDragOver
          ? 'border-primary border-dashed bg-primary/5 ring-2 ring-primary/20'
          : readOnly ? '' : 'border-gray-200 bg-white'}
      `}
      style={{ minHeight }}
    >
      {/* Read-only empty state — shown when the box is non-editable and no
           notes have been recorded yet. When notes exist they render as normal
           NoteCards below (visible but non-interactive). */}
      {readOnly && notes.length === 0 && (
        <div className="px-3.5 py-3 text-sm text-gray-400 italic">
          {readOnlyPlaceholder}
        </div>
      )}

      {/* Note cards with insertion indicator for drag reordering */}
      {notes.length > 0 && (
        <div ref={notesListRef} className="p-1.5 space-y-0.5">
          {notes.map((note, idx) => (
            <React.Fragment key={note.id}>
              {/* Insertion indicator — appears above this card when dragging */}
              {dropInsertIndex === idx && (
                <div
                  data-insert-indicator="true"
                  className="flex items-center gap-1 px-2 py-0"
                  style={{ height: '2px' }}
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-primary/40 flex-shrink-0" />
                  <div className="flex-1 h-px bg-primary/30" />
                </div>
              )}
              <NoteCard
                note={note}
                domainId={domainId}
                onUpdate={readOnly ? undefined : updateNote}
                onDelete={readOnly ? undefined : deleteNote}
                onUpdateTags={(readOnly && !allowTagWhileReadOnly) ? undefined : updateNoteTags}
                readOnly={readOnly}
                allowDragWhileReadOnly={allowDragWhileReadOnly}
                allowTagWhileReadOnly={allowTagWhileReadOnly}
                isExiting={exitingNoteIds ? exitingNoteIds.has(note.id) : false}
                isMapped={mappedNoteIds ? mappedNoteIds.has(note.id) : false}
                moveTargets={moveTargets}
                currentMoveId={currentMoveId}
                onMoveNoteTo={readOnly ? undefined : onMoveNoteTo}
                enableCriteriaTagging={enableCriteriaTagging}
                tagOptions={tagOptions}
                protectedTagId={protectedTagId}
                onRequestRemoveProtectedTag={onRequestRemoveProtectedTag}
              />
            </React.Fragment>
          ))}
          {/* Insertion indicator after the last card */}
          {dropInsertIndex === notes.length && (
            <div
              data-insert-indicator="true"
              className="flex items-center gap-1 px-2 py-0"
              style={{ height: '2px' }}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-primary/40 flex-shrink-0" />
              <div className="flex-1 h-px bg-primary/30" />
            </div>
          )}
        </div>
      )}

      {/* Drop hint when dragging over empty editor */}
      {isDragOver && notes.length === 0 && (
        <div className="flex items-center justify-center h-16 text-xs text-primary/60 font-medium">
          Drop note here
        </div>
      )}

      {/* New note input — hidden in readOnly mode */}
      {!readOnly && (
        <div className={`px-3 ${notes.length > 0 ? 'pb-2' : 'py-2'}`}>
          <div style={{ display: 'grid' }}>
            {/* Highlight layer — rendered HTML sits behind the textarea.
                 Trailing newlines are collapsed by browsers even with pre-wrap,
                 so we append a non-breaking space to force the last line open. */}
            <div
              aria-hidden="true"
              className="text-sm leading-snug py-1.5 whitespace-pre-wrap break-words pointer-events-none"
              style={{ gridArea: '1/1' }}
              dangerouslySetInnerHTML={{
                __html: (draftHighlightedHtml || '&nbsp;') + (draft.endsWith('\n') ? '\n\u00a0' : ''),
              }}
            />
            {/* Transparent textarea on top — captures all input. spellCheck stays on; CSS below
                 keeps browser spelling/grammar wavy lines visible over the highlight layer. */}
            <textarea
              ref={draftRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={1}
              spellCheck
              // Commit the in-progress draft when focus leaves the input. Without
              // this, text typed but not yet confirmed with Enter would be lost
              // when the clinician clicks away (e.g. a sidebar/footer nav button),
              // since the draft lives only in local component state until committed.
              // addNote() trims and no-ops on empty, so blurring an empty input is
              // harmless. This mirrors the edit-in-place NoteCard, which already
              // commits on blur.
              onBlur={addNote}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  addNote();
                  return;
                }
                if (e.key === 'Enter' && e.shiftKey) {
                  e.preventDefault();
                  const el = e.target;
                  const start = el.selectionStart;
                  const end = el.selectionEnd;
                  setDraft(prev => prev.slice(0, start) + '\n' + prev.slice(end));
                  requestAnimationFrame(() => el.setSelectionRange(start + 1, start + 1));
                  return;
                }
                // Backspace on an empty draft pulls the last committed note
                // back into the input for editing — like Gmail recipient chips
                // or tag inputs. Covers the "accidentally pressed Enter too
                // many times" case without needing a bulk-delete UI.
                if (e.key === 'Backspace' && !draft) {
                  const lastNote = notes[notes.length - 1];
                  if (lastNote) {
                    e.preventDefault();
                    setDraft(lastNote.text);
                    setDraftQuestions(lastNote.questions || []);
                    setDraftDomains(lastNote.domains || []);
                    // Preserve tags across undo → re-commit so a clinician who
                    // fat-fingers Enter does not lose their Tag selections.
                    setDraftCriteriaTags(
                      Array.isArray(lastNote.criteriaTags) ? lastNote.criteriaTags : undefined,
                    );
                    onChange(notes.slice(0, -1));
                  }
                }
              }}
              placeholder={notes.length === 0 ? placeholder : 'Add another note…'}
              className="structured-notes-spellcheck-input text-sm leading-snug caret-gray-700 placeholder:text-gray-300 bg-transparent resize-none py-1.5 focus:outline-none overflow-hidden"
              style={{ gridArea: '1/1', caretColor: '#374151' }}
            />
            {/* Bottom border sits below both layers */}
            <div style={{ gridArea: '1/1', alignSelf: 'end' }} className="border-b border-dashed border-gray-200" />
          </div>
        </div>
      )}

      {/* Keyframe for the "Delete?" confirm button entrance.
           structured-notes-spellcheck-input: typed text is transparent so the highlight
           layer underneath stays aligned with the caret, but we force browser spelling/
           grammar decorations to remain visible (otherwise color:transparent hides them). */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.9); }
          to   { opacity: 1; transform: scale(1); }
        }

        .structured-notes-spellcheck-input {
          color: transparent;
          -webkit-text-fill-color: transparent;
        }

        .structured-notes-spellcheck-input::spelling-error,
        .structured-notes-spellcheck-input::-webkit-spelling-error {
          text-decoration-line: underline;
          text-decoration-style: wavy;
          text-decoration-color: #dc2626;
          text-decoration-skip-ink: none;
          -webkit-text-decoration-line: underline;
          -webkit-text-decoration-style: wavy;
          -webkit-text-decoration-color: #dc2626;
        }

        .structured-notes-spellcheck-input::grammar-error,
        .structured-notes-spellcheck-input::-webkit-grammar-error {
          text-decoration-line: underline;
          text-decoration-style: wavy;
          text-decoration-color: #2563eb;
          text-decoration-skip-ink: none;
          -webkit-text-decoration-line: underline;
          -webkit-text-decoration-style: wavy;
          -webkit-text-decoration-color: #2563eb;
        }
      `}</style>
    </div>
  );
}
